import { useEffect } from 'react';
import Constants from 'expo-constants';
import * as Notifications from 'expo-notifications';
import { Platform } from 'react-native';
import { supabase } from '../lib/supabase';

type NotificationData = {
  conversationId?: string;
};

let foregroundHandlerConfigured = false;

const configureForegroundNotifications = () => {
  if (foregroundHandlerConfigured) return;

  Notifications.setNotificationHandler({
    handleNotification: async () => ({
      shouldPlaySound: true,
      shouldSetBadge: false,
      shouldShowBanner: true,
      shouldShowList: true,
    }),
  });

  foregroundHandlerConfigured = true;
};

const getProjectId = () =>
  Constants.expoConfig?.extra?.eas?.projectId ||
  Constants.easConfig?.projectId ||
  null;

export function usePushNotifications(
  userId: string | null | undefined,
  onConversationPress?: (conversationId: string) => void,
) {
  useEffect(() => {
    if (Platform.OS !== 'ios' || !userId) return;

    let isMounted = true;

    const registerPushToken = async () => {
      try {
        configureForegroundNotifications();

        const projectId = getProjectId();
        if (!projectId) {
          console.warn('Push notifications: missing Expo EAS project ID');
          return;
        }

        const { status: existingStatus } = await Notifications.getPermissionsAsync();
        let finalStatus = existingStatus;

        if (existingStatus !== 'granted') {
          const { status } = await Notifications.requestPermissionsAsync();
          finalStatus = status;
        }

        if (!isMounted || finalStatus !== 'granted') {
          return;
        }

        const tokenResponse = await Notifications.getExpoPushTokenAsync({ projectId });
        const token = tokenResponse.data;

        const { error } = await supabase.rpc('register_push_token', {
          p_token: token,
          p_platform: 'ios',
          p_device_info: {
            appVersion: Constants.expoConfig?.version ?? null,
            buildNumber: Constants.expoConfig?.ios?.buildNumber ?? null,
            appOwnership: Constants.appOwnership ?? null,
          },
        });

        if (error) {
          throw error;
        }
      } catch (error) {
        console.error('Push notifications: failed to register token', error);
      }
    };

    registerPushToken();

    return () => {
      isMounted = false;
    };
  }, [userId]);

  useEffect(() => {
    if (Platform.OS !== 'ios' || !userId || !onConversationPress) return;

    const handleResponse = (response: Notifications.NotificationResponse) => {
      const data = response.notification.request.content.data as NotificationData;
      if (data.conversationId) {
        onConversationPress(data.conversationId);
      }
    };

    const subscription = Notifications.addNotificationResponseReceivedListener(handleResponse);

    Notifications.getLastNotificationResponseAsync()
      .then((response) => {
        if (response) {
          handleResponse(response);
        }
      })
      .catch((error) => {
        console.error('Push notifications: failed to read launch response', error);
      });

    return () => {
      subscription.remove();
    };
  }, [onConversationPress, userId]);
}
